import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../../../shared/utils';
import { ConfirmationModal } from '../../admin/components/ConfirmationModal';
import sojaImg from '../../../assets/soja.png';
import maizImg from '../../../assets/maiz.png';
import trigoImg from '../../../assets/trigo.png';
import sorgoImg from '../../../assets/sorgo.png';
import girasolImg from '../../../assets/girasol.png';
import maniImg from '../../../assets/mani.png';
import { getCurrentSessionToken } from '../../auth';
import { 
  fetchMarketPrices, 
  publishMarketPrices, 
  seedMarketPrices,
  type MarketPrice
} from '../services/marketPriceService';

// --- Reusable Sub-component ---
interface GrainPriceCardProps {
  name: string;
  price: string;
  setPrice: (val: string) => void;
  savedPrice: number;
  iconBgClass: string;
  iconTextClass: string;
  lastUpdate: string;
  image: string;
  imageClass?: string;
}

const GrainPriceCard: React.FC<GrainPriceCardProps> = ({ 
  name, 
  price, 
  setPrice, 
  savedPrice, 
  iconBgClass, 
  iconTextClass,
  lastUpdate,
  image,
  imageClass = "object-cover"
}) => {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="p-4 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-border bg-[#E7F5E4]">
            <img src={image} alt={name} className="w-full h-full object-contain p-1.5" />
          </div>
          <div className="flex-1 flex items-start justify-between gap-2">
            <div>
              <h3 className="text-lg font-semibold">{name}</h3>
              <p className="text-xs text-muted-foreground">Precio por tonelada</p>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-[10px] font-medium text-muted-foreground mb-1">Últ. actualización</span>
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] rounded-full font-semibold bg-muted text-muted-foreground whitespace-nowrap">
                {lastUpdate}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 pt-0">
        <div className="space-y-2">
          <label className="block text-sm font-medium">Precio (ARS)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
            <input 
              type="number" 
              min="0"
              step="100"
              value={price}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setPrice(isNaN(val) ? '0' : String(Math.max(0, val)));
              }}
              className="w-full pl-8 pr-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="0"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Actual: <span>{formatCurrency(savedPrice)}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

const getGrainStyle = (cereal: string) => {
  switch (cereal.toLowerCase()) {
    case 'soja':
      return {
        bg: 'bg-[#5BBA47]/15',
        text: 'text-[#006837]',
        image: sojaImg
      };
    case 'maiz':
    case 'maíz':
      return {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        image: maizImg
      };
    case 'trigo':
      return {
        bg: 'bg-amber-100',
        text: 'text-amber-700',
        image: trigoImg
      };
    case 'sorgo':
      return {
        bg: 'bg-red-50 border-red-200',
        text: 'text-red-700',
        image: sorgoImg
      };
    case 'girasol':
      return {
        bg: 'bg-orange-50 border-orange-200',
        text: 'text-orange-700',
        image: girasolImg
      };
    case 'mani':
    case 'maní':
      return {
        bg: 'bg-blue-50 border-blue-200',
        text: 'text-blue-700',
        image: maniImg
      };
    default:
      return {
        bg: 'bg-green-50 border-green-200',
        text: 'text-green-700',
        image: trigoImg // fallback image
      };
  }
};

// --- Main Component ---
export const SettingsPage: React.FC = () => {
  const [pricesList, setPricesList] = useState<MarketPrice[]>([]);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [savedPrices, setSavedPrices] = useState<Record<string, number>>({});
  const [lastUpdates, setLastUpdates] = useState<Record<string, string>>({});

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const formatDate = (dateStr: string) => {
    try {
      let cleanDateStr = dateStr.trim();
      const hasTimezone = /z$/i.test(cleanDateStr) || /[+-]\d{2}:?\d{2}$/.test(cleanDateStr);
      if (!hasTimezone) {
        if (cleanDateStr.includes(' ')) {
          cleanDateStr = cleanDateStr.replace(' ', 'T');
        }
        cleanDateStr = `${cleanDateStr}Z`;
      }
      const d = new Date(cleanDateStr);
      if (isNaN(d.getTime())) return 'Sin actualizar';
      const day = d.getDate();
      const month = d.toLocaleDateString('es-AR', { month: 'short' }).replace('.', '');
      const year = d.getFullYear();
      const time = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
      return `${day} ${month} ${year}, ${time}`;
    } catch (e) {
      return 'Sin actualizar';
    }
  };

  const loadPrices = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      const token = await getCurrentSessionToken();
      if (!token) throw new Error("No active session token found");
      
      const data = await fetchMarketPrices(token);
      if (data && Array.isArray(data) && data.length > 0) {
        setPricesList(data);
        
        const initialInputs: Record<string, string> = {};
        const initialSavedPrices: Record<string, number> = {};
        const initialLastUpdates: Record<string, string> = {};
        
        data.forEach(item => {
          const key = item.cereal.toLowerCase();
          initialInputs[key] = String(item.price_per_ton);
          initialSavedPrices[key] = item.price_per_ton;
          initialLastUpdates[key] = formatDate(item.published_at);
        });

        setInputs(initialInputs);
        setSavedPrices(initialSavedPrices);
        setLastUpdates(initialLastUpdates);
      } else {
        setErrorMessage("Base de datos de precios vacía. Inicializa las tablas para comenzar.");
      }
    } catch (err: any) {
      console.error("Failed to load market prices:", err);
      setErrorMessage("Error al conectar con la base de datos de precios. Asegúrate de inicializar las tablas.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPrices();
  }, []);

  const handleSeedDatabase = async () => {
    try {
      setIsSaving(true);
      setErrorMessage('');
      const token = await getCurrentSessionToken();
      if (!token) throw new Error("No active session token found");
      
      await seedMarketPrices(token);
      
      // Publish default starter prices
      await publishMarketPrices([
        { cereal: 'soja', price: 285000 },
        { cereal: 'maiz', price: 195000 },
        { cereal: 'trigo', price: 245000 }
      ], token);

      await loadPrices();
    } catch (err: any) {
      console.error("Failed to seed market prices:", err);
      setErrorMessage("Error al inicializar la base de datos de precios de pizarra.");
    } finally {
      setIsSaving(false);
    }
  };

  const getChanges = () => {
    const changes: { name: string; old: number; new: number }[] = [];
    pricesList.forEach(item => {
      const key = item.cereal.toLowerCase();
      const inputVal = parseFloat(inputs[key] || '0');
      const savedVal = savedPrices[key] || 0;
      if (inputVal !== savedVal) {
        changes.push({ 
          name: item.cereal.charAt(0).toUpperCase() + item.cereal.slice(1), 
          old: savedVal, 
          new: inputVal 
        });
      }
    });
    return changes;
  };

  const handlePublishClick = () => {
    const changes = getChanges();
    if (changes.length === 0) return;
    setIsConfirmOpen(true);
  };

  const confirmPublish = async () => {
    try {
      setIsSaving(true);
      setErrorMessage('');
      const token = await getCurrentSessionToken();
      if (!token) throw new Error("No active session token found");

      const changesList = getChanges();
      if (changesList.length === 0) {
        setIsConfirmOpen(false);
        return;
      }

      const payload = changesList.map(c => ({
        cereal: c.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""),
        price: c.new
      }));

      await publishMarketPrices(payload, token);

      await loadPrices();
      setIsConfirmOpen(false);
    } catch (err: any) {
      console.error("Failed to publish market prices:", err);
      setErrorMessage("Error al publicar los nuevos precios de pizarra.");
    } finally {
      setIsSaving(false);
    }
  };

  const changes = getChanges();

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <p className="text-sm text-muted-foreground font-semibold">Cargando precios de pizarra...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#212121]">Ajustes</h1>
          <p className="text-muted-foreground mt-1">
            Actualiza los precios por tonelada de cada grano
          </p>
        </div>
      </div>

      {errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm font-semibold flex items-center justify-between gap-3">
          <span>{errorMessage}</span>
          <button onClick={() => setErrorMessage('')} className="text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {pricesList.map(item => {
          const key = item.cereal.toLowerCase();
          const style = getGrainStyle(item.cereal);
          const displayName = item.cereal.charAt(0).toUpperCase() + item.cereal.slice(1);
          
          return (
            <GrainPriceCard 
              key={item.id}
              name={displayName}
              price={inputs[key] || '0'}
              setPrice={(val) => setInputs(prev => ({ ...prev, [key]: val }))}
              savedPrice={savedPrices[key] || 0}
              iconBgClass={style.bg}
              iconTextClass={style.text}
              lastUpdate={lastUpdates[key] || 'Sin actualizar'}
              image={style.image}
            />
          );
        })}
      </div>

      <div className="flex justify-end pt-4">
        <button 
          onClick={handlePublishClick}
          disabled={changes.length === 0 || isSaving}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
            changes.length > 0 && !isSaving
              ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          {isSaving ? (
            <svg className="animate-spin h-4 w-4 text-muted-foreground" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
            </svg>
          )}
          {isSaving ? 'Guardando...' : 'Publicar Precios'}
        </button>
      </div>

      <ConfirmationModal 
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={confirmPublish}
        title="Confirmar Cambio de Precios"
        confirmText={isSaving ? "Publicando..." : "Publicar Precios"}
        type="success" 
        description={
          <div className="space-y-3">
            <p>¿Estás seguro que deseas actualizar los precios de pizarra?</p>
            <div className="bg-muted/30 rounded-lg p-3 space-y-2 border">
              {changes.map(c => (
                <div key={c.name} className="flex justify-between items-center text-sm">
                  <span className="font-semibold text-[#212121]">{c.name}:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground line-through">{formatCurrency(c.old)}</span>
                    <svg className="w-3 h-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                    </svg>
                    <span className="font-bold text-[#006837]">{formatCurrency(c.new)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        }
      />
    </div>
  );
};
